import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { IconPresentationProps } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import useEmblaCarousel from "embla-carousel-react";
import parse from "html-react-parser";
import React, { useCallback, useEffect, useState } from "react";
import { css } from "styled-components";

interface SlideModel {
    fields: {
        slideTitle: string;
        background: "image" | "color";
        image: string;
        imageOverlay: string;
        partialOverlay: boolean;
        partialOverlayPositioning: "top" | "middle" | "bottom";
        responsiveImageBehavior: "cover" | "center" | "prioritizeHeight" | "prioritizeWidth";
        backgroundColor: string;
        smallText: string;
        heading: string;
        subheading: string;
        buttonLabel: string;
        buttonLink: LinkFieldValue;
        buttonVariant: "primary" | "secondary" | "tertiary";
        secondaryButtonLabel: string;
        secondaryButtonLink: LinkFieldValue;
        focalPoint:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        contentPadding: number;
        centerTextVertically: boolean;
        customCSS: string;
    };
}

const enum fields {
    height = "height",
    textAlignment = "textAlignment",
    showArrows = "showArrows",
    slideIndicator = "slideIndicator",
    autoplay = "autoplay",
    slides = "slides",
    responsiveFontSizes = "responsiveFontSizes",
    customFontSizes = "customFontSizes",
    h1FontSize = "h1FontSize",
    h2FontSize = "h2FontSize",
    h3FontSize = "h3FontSize",
    h4FontSize = "h4FontSize",
    h5FontSize = "h5FontSize",
    h6FontSize = "h6FontSize",
    normalFontSize = "normalFontSize",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.height]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.textAlignment]: "left" | "center" | "right";
        [fields.showArrows]: boolean;
        [fields.slideIndicator]: boolean;
        [fields.autoplay]: number;
        [fields.responsiveFontSizes]: boolean;
        [fields.customFontSizes]: boolean;
        [fields.h1FontSize]: number;
        [fields.h2FontSize]: number;
        [fields.h3FontSize]: number;
        [fields.h4FontSize]: number;
        [fields.h5FontSize]: number;
        [fields.normalFontSize]: number;
        [fields.slides]: SlideModel[];
        [fields.customCSS]: string;
    };
    extendedStyles?: SlideshowStyles;
}

export interface SlideshowStyles {
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    smallText?: TypographyPresentationProps;
    headingText?: TypographyPresentationProps;
    subheadingText?: TypographyPresentationProps;
    slideButton?: ButtonPresentationProps;
    slideSecondaryButton?: ButtonPresentationProps;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
}

export const slideshowStyles: SlideshowStyles = {
    slideshowWrapper: {
        css: css`
            position: relative;
            width: 100vw;
            margin-left: calc(-50vw + 50%);
            margin-bottom: 30px;
            border-bottom: ${getColor("primary.main")} solid 4px;
        `,
    },
    prevArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            margin-top: -10px;

            @media (max-width:768px){
                display: none;
            }
        `,
    },
    prevArrowButton: {
        css: css`
            background-color: ${getColor("primary.main")};
            width: 48px;
            height: 48px;
            display: inline-flex;
            justify-content: center;
            align-items: center;
            border: 0;
            border-radius: 100%;
            padding: 0;
            margin-left: 35%;

            span {
                color: ${getColor("primary.contrast")} !important;
                width: 24px;
                height: 24px;
            }

            &:hover {
                background-color: ${getColor("primary.contrast")} !important;

                span{
                    color: ${getColor("primary.main")} !important;
                }
            }

            &:focus {
                outline: none;
            }
        `,
    },
    iconProps: {
        size: 35,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
        `,
    },
    slideContentWrapper: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            position: relative;
            color: white;
            background-repeat: no-repeat;
        `,
    },
    slideOverlayWrapper: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: flex-end;
            align-items: flex-end;
        `,
    },
    slideButton: {
        css: css`
            width: fit-content;
            background-color: ${getColor("primary.contrast")};
            margin-right: 16px;

            @media (max-width:768px){
                width: 100%;

                margin-right: 0;
                margin-bottom: 16px;
            }

            &:hover {
            
                span {
                    color: ${getColor("primary.contrast")};
                }
            }
        `,
    },
    slideSecondaryButton: {
        css: css`
            width: fit-content;
            background-color: ${getColor("primary.main")};

            @media (max-width:768px){
                width: 100%;
            }

            span {
                color: ${getColor("primary.contrast")} !important;
            }
            
            &:hover {
                background-color: ${getColor("primary.contrast")} !important;

                span {
                    color: ${getColor("primary.main")} !important;
                }
            }
        `,
    },
    dotsContainerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            margin-top: -40px;
            position: absolute;
            width: 100%;

            @media (max-width: 415px) {
                margin-top: -35px;
            }

            @media (max-width: 376px) {
                margin-top: -30px;
            }
        `,
    },
    dotButton: {
        css: css`
            width: 8px;
            height: 8px;
            padding: 0;
            margin: 0 6px;
            border: 0;
            border-radius: 100%;
            background-color: ${getColor("primary.contrast")};
            opacity: 0.5;
            min-height: inherit;

            &:hover,
            &:active {
                background-color: ${getColor("primary.main")};
                opacity: 1;
            }

            &:focus {
                outline: none;
            }
        `,
    },
    nextArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            margin-top: -10px;
            right: 0;

            @media (max-width:768px){
                display: none;
            }
        `,
    },
    nextArrowButton: {
        css: css`
            background-color: ${getColor("primary.main")};
            width: 48px;
            height: 48px;
            display: inline-flex;
            justify-content: center;
            align-items: center;
            border: 0;
            border-radius: 100%;
            padding: 0;
            margin-left: -50%;

            span {
                color: ${getColor("primary.contrast")} !important;
                width: 24px;
                height: 24px;
            }

            &:hover {
                background-color: ${getColor("primary.contrast")} !important;

                span{
                    color: ${getColor("primary.main")} !important;
                }
            }

            &:focus {
                outline: none;
            }
        `,
    },
    smallText: {
        css: css`
            font-size: 12px;
            font-weight: 600;
            color: ${getColor("primary.main")};
            text-transform: uppercase;
            position: relative;
            display: block;
            padding-left: 16px;
            margin-bottom: 22px;
        
            &:before {
                content: "";
                position: absolute;
                background-color: ${getColor("primary.main")};
                width: 4px;
                height: 100%;
                top: 0;
                left: 0;
            }

            p {
                font-size: 12px;
                font-weight: 600;
                color: ${getColor("primary.main")};
                text-transform: uppercase;
            }
        `,
    },
    headingText: {
        css: css`

            position: relative;
            padding-left: 14px;
            margin-bottom: 22px;
        
            &:before {
                content: "";
                position: absolute;
                background-color: ${getColor("primary.contrast")};
                width: 4px;
                height: 100%;
                top: 0;
                left: 0;
            }

            p {
                font-size: 56px ;
                font-weight: bold;
                line-height: 64px;

                @media (max-width:768px){
                    font-size: 44px;
                    line-height: 52px;
                }
            }
        `,
    },
    subheadingText: {
        css: css`
            margin-bottom: 22px;

            p {
                font-size: 18px;
            }
        `,

    },
    slideCenteringWrapperStyles: {
        css: css`
            margin: 0 60px 40px 0;
            max-width: 590px;
        `,
    }
};

const defaultCustomCss = `.slideshow-wrapper {
}

.slideshow-carousel {
}

.slideshow-slide-container {
}

.slide-wrapper {
}

.slide-heading {
}

.slide-subheading {
}

.slide-button {
}

.slide-indicator {
}

.slide-dot-button {
}

.slide-arrow-wrapper {
}

.slide-arrow {
}
`;

export const Slideshow = ({ fields, extendedStyles }: OwnProps) => {
    const { isInShell } = useShellContext();
    const history = useHistory();
    const { links: buttonLinks } = useGetLinks(fields.slides, o => o.fields.buttonLink);
    const { links: secondaryButtonLinks } = useGetLinks(fields.slides, o => o.fields.secondaryButtonLink || { type: "Page", value: "" });
    const styles = useMergeStyles("slideshow", slideshowStyles, extendedStyles);

    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });
    }, [embla]);

    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }

        embla.scrollNext();
    }, [embla]);

    useRecursiveTimeout(scrollNext, fields.autoplay * 1000);

    useEffect(() => {
        if (!embla || !fields.slides || fields.slides.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            loop: true,
        });
    }, [embla, fields.slides]);

    const onClick = (url: string | undefined) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    if (!fields.slides || fields.slides.length === 0) {
        return null;
    }

    let heightStyles: string;
    switch (fields.height) {
        case "1/4 viewport":
            heightStyles = `
                @media (min-width: 375px) {
                    min-height: 570px;
                }

                @media (min-width: 414px) {
                    min-height: 540px;
                }

                @media (min-width: 576px) {
                    min-height: 480px;
                }
                
                @media (min-width: 768px) {
                    min-height: 480px;
                }
                
                @media (min-width: 992px) {
                    min-height: 480px;
                }
                
                @media (min-width: 1024px) {
                    min-height: 480px;
                }
                
                @media (min-width: 1200px) {
                    min-height: 432px;
                }
                
                @media (min-width: 1280px) {
                    min-height: 432px;
                }
                
                @media (min-width: 1366px) {
                    min-height: 450px;
                }
                
                @media (min-width: 1536px) {
                    min-height: 483px;
                }
                
                @media (min-width: 1600px) {
                    min-height: 496px;
                }
                
                @media (min-width: 1680px) {
                    min-height: 512px;
                }
                
                @media (min-width: 1920px) {
                    min-height: 560px;
                }
                
                @media (min-width: 2560px) {
                    min-height: 688px;
                }
                
                @media (min-width: 3840px) {
                    min-height: 708px;
                }
            `;
            break;
        case "1/2 viewport":
            heightStyles = "min-height: 50vh;";
            break;
        case "3/4 viewport":
            heightStyles = "min-height: 75vh;";
            break;
        case "fullViewport":
            heightStyles = "min-height: 100vh;";
            break;
    }

    const textAlignStyles = `text-align: ${fields.textAlignment};`;

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="slideshow-wrapper" {...styles.slideshowWrapper}>
                {fields.showArrows && fields.slides.length > 1 && (
                    <StyledWrapper {...styles.prevArrowWrapper}>
                        <Button
                            {...styles.prevArrowButton}
                            onClick={() => embla && embla.scrollPrev()}
                            aria-label={translate("Previous")}
                        >
                            <ButtonIcon {...styles.iconProps} src={ChevronLeft} />
                        </Button>
                    </StyledWrapper>
                )}
                <StyledWrapper className="slideshow-carousel" {...styles.carouselContainer} ref={emblaRef}>
                    <StyledWrapper className="slideshow-slide-container" {...styles.slideContainerWrapper}>
                        {fields.slides.map((slide, index) => {
                            const buttonLink = buttonLinks[index];
                            const secondaryButtonLink = secondaryButtonLinks[index];

                            let responsiveImageBehaviorStyles;
                            if (slide.fields.responsiveImageBehavior === "cover") {
                                responsiveImageBehaviorStyles = "background-size: cover;";
                            } else if (slide.fields.responsiveImageBehavior === "center") {
                                responsiveImageBehaviorStyles = "background-size: contain;";
                            } else if (slide.fields.responsiveImageBehavior === "prioritizeHeight") {
                                responsiveImageBehaviorStyles = "background-size: auto 100%;";
                            } else if (slide.fields.responsiveImageBehavior === "prioritizeWidth") {
                                responsiveImageBehaviorStyles = "background-size: 100% auto;";
                            }

                            const backgroundStyles =
                                slide.fields.background === "image"
                                    ? `background-image: url(${parseImageUrl(slide.fields.image).url});`
                                    : `background-color: ${slide.fields.backgroundColor};`;
                            const focalPointStyles = getFocalPointStyles(slide.fields.focalPoint);

                            let overlayPositioningStyles;
                            if (!slide.fields.partialOverlay) {
                                overlayPositioningStyles = "align-items: stretch;";
                            } else if (slide.fields.partialOverlayPositioning === "top") {
                                overlayPositioningStyles = "align-items: flex-start;";
                            } else if (slide.fields.partialOverlayPositioning === "middle") {
                                overlayPositioningStyles = "align-items: center;";
                            } else if (slide.fields.partialOverlayPositioning === "bottom") {
                                overlayPositioningStyles = "align-items: flex-end;";
                            }

                            let fontSizeStyles;
                            if (fields.responsiveFontSizes || fields.customFontSizes) {
                                fontSizeStyles = responsiveStyleRules(
                                    fields.responsiveFontSizes,
                                    fields.customFontSizes ? fields : undefined,
                                );
                            }

                            const slideWrapperStyles = {
                                css: css`
                                    ${styles.slideContentWrapper?.css || ""}
                                    ${heightStyles}
                                    ${textAlignStyles}
                                    ${backgroundStyles}
                                    ${responsiveImageBehaviorStyles}
                                    ${focalPointStyles}
                                    ${overlayPositioningStyles}
                                    ${fontSizeStyles}
                                    align-items: stretch;
                                `,
                            };
                            const slideOverlayWrapperStyles = {
                                css: css`
                                    ${styles.slideOverlayWrapper?.css || ""}
                                    background-color: ${slide.fields.background === "image"
                                        ? slide.fields.imageOverlay
                                        : ""};
                                    padding: ${slide.fields.contentPadding}px;
                                    ${slide.fields.centerTextVertically &&
                                    `
                                        display: flex;
                                        align-items: center;
                                        background: linear-gradient(270deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.5) 50%, rgba(0, 0, 0, 0) 100%),
                                        linear-gradient(0deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0) 100%);`}
                                    align-items: flex-end;
                                `,
                            };
                            return (
                                <StyledWrapper className="slide-wrapper" key={index} {...slideWrapperStyles}>
                                    <StyledWrapper {...slideOverlayWrapperStyles}>
                                        <StyledWrapper {...styles.slideCenteringWrapperStyles}>
                                            {slide.fields.smallText && (
                                                <Typography {...styles.smallText}>
                                                    {parse(slide.fields.smallText, parserOptions)}
                                                </Typography>
                                            )}
                                            {slide.fields.heading && (
                                                <Typography as="h2" className="slide-heading" {...styles.headingText}>
                                                    {parse(slide.fields.heading, parserOptions)}
                                                </Typography>
                                            )}
                                            {slide.fields.subheading && (
                                                <Typography as="p" className="slide-subheading" {...styles.subheadingText}>
                                                    {parse(slide.fields.subheading, parserOptions)}
                                                </Typography>
                                            )}
                                            {(slide.fields.buttonLabel || slide.fields.buttonLink.value) && (
                                                <Button
                                                    className="slide-button"
                                                    {...styles.slideButton}
                                                    variant={slide.fields.buttonVariant}
                                                    icon={{ src: ChevronRight, position: "right" }}
                                                    onClick={() => onClick(buttonLink?.url)}
                                                >
                                                    {slide.fields.buttonLabel || buttonLink?.title || buttonLink?.url}
                                                </Button>
                                            )}
                                            {(slide.fields.secondaryButtonLabel || slide.fields.secondaryButtonLink?.value) && (
                                                <Button
                                                    icon={{ src: ChevronRight, position: "right" }}
                                                    {...styles.slideSecondaryButton}
                                                    onClick={() => onClick(secondaryButtonLink?.url)}
                                                >
                                                    {slide.fields.secondaryButtonLabel || secondaryButtonLink?.title || secondaryButtonLink?.url}
                                                </Button>
                                            )}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </StyledWrapper>
                            );
                        })}
                    </StyledWrapper>
                </StyledWrapper>
                {fields.slideIndicator && (
                    <StyledWrapper className="slide-indicator" {...styles.dotsContainerWrapper}>
                        {fields.slides.map((_, i) => {
                            const dotColorStyles =
                                i === selectedIndex
                                    ? css`
                                        background-color: ${getColor("primary.main")};
                                        opacity: 1;
                                        width: 32px;
                                        border-radius: 0;
                                        &:hover { background: white; }
                                    `
                                    : "";
                            const dotButtonStyles = {
                                css: css`
                                    ${styles.dotButton?.css || ""}
                                    ${dotColorStyles}
                                `,
                            };

                            return (
                                <Button
                                    className="slide-dot-button"
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={i}
                                    {...dotButtonStyles}
                                    onClick={() => embla && embla.scrollTo(i)}
                                    aria-label={translate("Scroll to {0}", `${i + 1}`)}
                                />
                            );
                        })}
                    </StyledWrapper>
                )}
                {fields.showArrows && fields.slides.length > 1 && (
                    <StyledWrapper className="slide-arrow-wrapper" {...styles.nextArrowWrapper}>
                        <Button
                            className="slide-arrow"
                            {...styles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                            aria-label={translate("Next")}
                        >
                            <ButtonIcon {...styles.iconProps} src={ChevronRight} />
                        </Button>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: Slideshow,
    definition: {
        group: "Basic",
        icon: "presentation-screen",
        fieldDefinitions: [
            {
                name: fields.height,
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                ],
                defaultValue: "1/4 viewport",
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                name: fields.textAlignment,
                editorTemplate: "DropDownField",
                defaultValue: "center",
                fieldType: "General",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                name: fields.showArrows,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.slideIndicator,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.autoplay,
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
                tab: settingsTab,
                min: 1,
            },
            {
                name: fields.responsiveFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: true,
            },
            {
                name: fields.customFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: false,
            },
            {
                name: fields.normalFontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h1FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: 40,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h2FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: 32,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h3FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h4FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h5FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h6FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.slides,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { slideTitle } }) => slideTitle || "Slide",
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "slideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "Slide",
                    },
                    {
                        name: "background",
                        displayName: "Background",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Image", value: "image" },
                            { displayName: "Color", value: "color" },
                        ],
                        defaultValue: "image",
                        hideEmptyOption: true,
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "imageOverlay",
                        displayName: "Image Color Overlay",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "partialOverlay",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "partialOverlayPositioning",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Top", value: "top" },
                            { displayName: "Middle", value: "middle" },
                            { displayName: "Bottom", value: "bottom" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "bottom",
                        isVisible: widget => widget.fields.partialOverlay,
                    },
                    {
                        name: "responsiveImageBehavior",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Cover", value: "cover" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Prioritize height", value: "prioritizeHeight" },
                            { displayName: "Prioritize width", value: "prioritizeWidth" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "cover",
                    },
                    {
                        name: "backgroundColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Color",
                        defaultValue: "black",
                        isVisible: widget => widget.fields.background === "color",
                    },
                    {
                        name: "focalPoint",
                        displayName: "Focal Point",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Top Left", value: "topLeft" },
                            { displayName: "Top Center", value: "topCenter" },
                            { displayName: "Top Right", value: "topRight" },
                            { displayName: "Center Left", value: "centerLeft" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Center Right", value: "centerRight" },
                            { displayName: "Bottom Left", value: "bottomLeft" },
                            { displayName: "Bottom Center", value: "bottomCenter" },
                            { displayName: "Bottom Right", value: "bottomRight" },
                        ],
                        defaultValue: "center",
                        hideEmptyOption: true,
                    },
                    {
                        name: "smallText",
                        editorTemplate: "TextField",
                        displayName: "Small Text",
                        defaultValue: "",
                    },
                    {
                        name: "heading",
                        editorTemplate: "RichTextField",
                        displayName: "Heading",
                        defaultValue: "",
                        extendedConfig: { height: 100 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                    },
                    {
                        name: "subheading",
                        editorTemplate: "RichTextField",
                        displayName: "Subheading",
                        defaultValue: "",
                        extendedConfig: { height: 170 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                    },
                    {
                        name: "buttonLabel",
                        editorTemplate: "TextField",
                        displayName: "Button Label",
                        defaultValue: "",
                    },
                    {
                        name: "buttonLink",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "buttonVariant",
                        displayName: "Button Variant",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Primary", value: "primary" },
                            { displayName: "Secondary", value: "secondary" },
                            { displayName: "Tertiary", value: "tertiary" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "primary",
                    },
                    {
                        name: "secondaryButtonLabel",
                        editorTemplate: "TextField",
                        displayName: "Secondary Button Label",
                        defaultValue: "",
                    },
                    {
                        name: "secondaryButtonLink",
                        displayName: "Secondary Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "contentPadding",
                        editorTemplate: "IntegerField",
                        defaultValue: 50,
                    },
                    {
                        name: "centerTextVertically",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;